<template>
  <Modal
    :show="show"
    title="Send InMail"
    size="md"
    :dialog-max-width="'560px'"
    :closeOnBackdrop="false"
    :backdrop-opacity="0.3"
    @close="emit('close')"
  >
    <template #header>
      <div class="inmail-modal__header-inner">
        <h2 id="modal-title" class="inmail-modal__title">Send InMail</h2>
        <p v-if="contactName" class="inmail-modal__subtitle">
          Compose and send a direct message to {{ contactName }}
        </p>
      </div>
    </template>

    <div class="inmail-modal__body">
      <div v-if="contact" class="inmail-modal__recipient">
        <div class="inmail-modal__recipient-info">
          <div class="inmail-modal__recipient-avatar">
            <Icon icon="mdi:account-circle" />
          </div>
          <div class="inmail-modal__recipient-details">
            <div class="inmail-modal__recipient-name">
              {{ contactName }}
            </div>
            <div class="inmail-modal__recipient-title">
              {{ contactTitle }} {{ contactCompany ? `@ ${contactCompany}` : "" }}
            </div>
          </div>
        </div>
        <div v-if="inmailBalance" class="inmail-modal__credits-badge">
          {{ getTotalCredits(inmailBalance) }} InMail credits remaining
        </div>
      </div>

      <div class="inmail-modal__form-group">
        <label for="subject" class="inmail-modal__label">
          Subject
          <span class="inmail-modal__required">(required)</span>
        </label>
        <input
          id="subject"
          v-model="subjectText"
          type="text"
          class="inmail-modal__input"
          placeholder="Enter subject"
          :disabled="isLoading"
          required
        />
      </div>

      <div class="inmail-modal__form-group">
        <label for="message" class="inmail-modal__label"> Message </label>
        <div class="inmail-modal__textarea-wrapper">
          <textarea
            id="message"
            v-model="messageText"
            class="inmail-modal__textarea"
            placeholder="Type your message here"
            :disabled="isLoading"
            rows="8"
          ></textarea>
        </div>

        <div class="inmail-modal__ai-row">
          <div class="inmail-modal__ai-buttons">
            <button
              ref="aiComposeButtonRef"
              type="button"
              class="inmail-modal__ai-btn inmail-modal__ai-btn--write"
              :disabled="isGeneratingAiCompose"
              :title="isGeneratingAiCompose ? 'Generating...' : 'AI Compose'"
              @click="toggleAiComposePanel"
            >
              <Icon icon="mdi:sparkles" class="inmail-modal__ai-btn-icon" />
              <span class="inmail-modal__ai-btn-text">AI Compose</span>
            </button>
            <button
              ref="aiRefineButtonRef"
              type="button"
              class="inmail-modal__ai-btn inmail-modal__ai-btn--refine"
              :disabled="isGeneratingAiCustom"
              :title="isGeneratingAiCustom ? 'Generating...' : 'AI Refine'"
              @click="toggleAiRefinePanel"
            >
              <Icon icon="mdi:sparkles" class="inmail-modal__ai-btn-icon" />
              <span class="inmail-modal__ai-btn-text">AI Refine</span>
            </button>
          </div>
        </div>

        <AiRefinePopover
          :show="showAiComposePanel"
          :trigger-el="aiComposeButtonRef"
          :disabled="isGeneratingAiCompose"
          title="AI Compose"
          generate-button-label="Generate Message"
          :show-attachments="false"
          @generate="handleAiComposeGenerate"
          @close="showAiComposePanel = false"
        />

        <AiRefinePopover
          :show="showAiPanel"
          :trigger-el="aiRefineButtonRef"
          :disabled="isGeneratingAiCustom"
          @generate="handleAiCustomizationGenerate"
          @close="showAiPanel = false"
        />
      </div>

      <div v-if="error" class="inmail-modal__error">
        <Icon icon="mdi:alert-circle" class="inmail-modal__error-icon" />
        <span>{{ error }}</span>
      </div>
    </div>

    <template #footer>
      <div class="inmail-modal__actions">
        <Button variant="secondary" :disabled="isLoading" @click="emit('close')"> Cancel </Button>
        <Button
          variant="primary"
          icon="mdi:send"
          icon-position="left"
          :loading="isLoading"
          :disabled="isLoading || !canSend"
          @click="handleSend"
        >
          Send
        </Button>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { Icon } from "@iconify/vue";
import { ref, computed, watch } from "vue";
import { Button, Modal } from "@/components/ui";
import Unipile from "@/services/unipile/unipile";
import AiRefinePopover from "@/components/socials/AiRefinePopover.vue";
import AIGenerationService from "@/services/ai/ai-generation";
import { useToast } from "@/composables/ui/useToast";
import { useResolvedAiPrompts } from "@/composables/useAiPrompts";

interface Props {
  show: boolean;
  contact?: any;
  accountId?: string;
  providerId?: string;
  icpId?: string;
  initialSubject?: string;
  initialBody?: string;
  inmailBalance?: {
    premium?: number;
    recruiter?: number;
    sales_navigator?: number;
    activeSubscriptionType?: "sales_navigator" | "recruiter" | "classic" | null;
    activeCredits?: number;
  } | null;
}

const props = withDefaults(defineProps<Props>(), {
  contact: null,
  accountId: undefined,
  providerId: undefined,
  icpId: undefined,
  initialSubject: undefined,
  initialBody: undefined,
  inmailBalance: null,
});
const $toast = useToast();

const emit = defineEmits<{
  close: [];
  sent: [];
}>();

const subjectText = ref("");
const messageText = ref("");
const isLoading = ref(false);
const error = ref<string | null>(null);
const showAiPanel = ref(false);
const showAiComposePanel = ref(false);
const isGeneratingAiCustom = ref(false);
const isGeneratingAiCompose = ref(false);
const aiRefineButtonRef = ref<HTMLElement | null>(null);
const aiComposeButtonRef = ref<HTMLElement | null>(null);
const resolvedAiPromptsQuery = useResolvedAiPrompts();

const toggleAiRefinePanel = () => {
  showAiComposePanel.value = false;
  showAiPanel.value = !showAiPanel.value;
};

const toggleAiComposePanel = () => {
  showAiPanel.value = false;
  showAiComposePanel.value = !showAiComposePanel.value;
};

const handleAiCustomizationGenerate = async (payload: {
  customInstructions: string;
  attachments: File[];
}) => {
  if (isGeneratingAiCustom.value) return;
  isGeneratingAiCustom.value = true;
  try {
    const response = await AIGenerationService.generateRefine({
      contactId: props.contact?.id,
      icpId: props.icpId,
      channel: "linkedin",
      currentDraft: messageText.value,
      customInstructions: payload.customInstructions,
      files: payload.attachments,
    });
    const text = response.body;
    messageText.value = text;
    showAiPanel.value = false;
    $toast.success("Message refined");
  } catch (err: any) {
    $toast.error(err?.response?.data?.message || "Failed to refine message");
  } finally {
    isGeneratingAiCustom.value = false;
  }
};

const handleAiComposeGenerate = async (payload: { customInstructions: string }) => {
  if (isGeneratingAiCompose.value) return;
  isGeneratingAiCompose.value = true;
  try {
    const response = await AIGenerationService.generateCompose({
      contactId: props.contact?.id,
      icpId: props.icpId,
      channel: "linkedin",
      customInstructions: payload.customInstructions,
      aiPromptCompose: resolvedAiPromptsQuery.composeAiPrompt.value,
    });
    messageText.value = response.body;
    showAiComposePanel.value = false;
    $toast.success("Message composed");
  } catch (err: any) {
    $toast.error(err?.response?.data?.message || "Failed to generate message");
  } finally {
    isGeneratingAiCompose.value = false;
  }
};

const contactName = computed(() => {
  if (!props.contact) return "";
  const fname = props.contact.fname || "";
  const lname = props.contact.lname || "";
  return `${fname} ${lname}`.trim() || "Unknown";
});

const contactTitle = computed(() => {
  return props.contact?.jobTitle || "";
});

const contactCompany = computed(() => {
  return props.contact?.company?.name || props.contact?.company || "";
});

const canSend = computed(() => {
  return subjectText.value.trim().length > 0 && props.accountId && props.providerId;
});

const getTotalCredits = (balance: any) => {
  if (!balance) return 0;
  if (balance.activeCredits !== undefined) {
    return balance.activeCredits;
  }
  return (balance.premium || 0) + (balance.recruiter || 0) + (balance.sales_navigator || 0);
};

watch(
  () => props.show,
  (isShowing) => {
    if (isShowing) {
      subjectText.value = props.initialSubject || "";
      messageText.value = props.initialBody || "";
      error.value = null;
      showAiPanel.value = false;
      showAiComposePanel.value = false;
    }
  }
);

const handleSend = async () => {
  if (!canSend.value || isLoading.value) return;

  error.value = null;
  isLoading.value = true;

  try {
    if (!props.accountId || !props.providerId) {
      throw new Error("Account ID and Provider ID are required");
    }
    // Pick LinkedIn API type based on subscription priority
    let linkedinApi: "classic" | "recruiter" | "sales_navigator" = "classic";
    if (props.inmailBalance?.activeSubscriptionType) {
      if (props.inmailBalance.activeSubscriptionType === "sales_navigator") {
        linkedinApi = "sales_navigator";
      } else if (props.inmailBalance.activeSubscriptionType === "recruiter") {
        linkedinApi = "recruiter";
      }
    } else if (props.inmailBalance) {
      if (props.inmailBalance.sales_navigator && props.inmailBalance.sales_navigator > 0) {
        linkedinApi = "sales_navigator";
      } else if (props.inmailBalance.recruiter && props.inmailBalance.recruiter > 0) {
        linkedinApi = "recruiter";
      }
    }

    const requestPayload: any = {
      account_id: props.accountId,
      attendees_ids: [props.providerId],
      subject: subjectText.value.trim(),
      text: messageText.value.trim() || undefined,
      linkedin: {
        inmail: true,
        api: linkedinApi,
      },
      contactId: props.contact?.id || undefined,
    };

    const response = await Unipile.createGroupChat(requestPayload);

    if (response?.data?.status && response?.data?.data) {
      emit("sent");
    } else {
      throw new Error("Failed to send InMail");
    }
  } catch (err: any) {
    console.error("Failed to send InMail:", err);
    error.value =
      err.response?.data?.message ||
      err.response?.data?.errorMessage ||
      err.message ||
      "Failed to send InMail. Please try again.";
  } finally {
    isLoading.value = false;
  }
};
</script>

<style scoped>
.inmail-modal__header-inner {
  flex: 1;
  min-width: 0;
}

.inmail-modal__title {
  margin: 0 0 4px 0;
  font-size: 1.125rem;
  font-weight: 700;
  color: #1a1a2e;
}

.inmail-modal__subtitle {
  margin: 0;
  font-size: 0.8125rem;
  color: #646464;
  line-height: 1.4;
}

.inmail-modal__body {
  padding: 20px 24px;
  overflow-y: auto;
}

.inmail-modal__recipient {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: #f9fafb;
  border-radius: 8px;
  margin-bottom: 20px;
}

.inmail-modal__recipient-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.inmail-modal__recipient-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-size: 1.5rem;
  flex-shrink: 0;
}

.inmail-modal__recipient-details {
  flex: 1;
  min-width: 0;
}

.inmail-modal__recipient-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1a1a2e;
  margin-bottom: 2px;
}

.inmail-modal__recipient-title {
  font-size: 0.75rem;
  color: #6b7280;
}

.inmail-modal__credits-badge {
  padding: 6px 12px;
  background: linear-gradient(135deg, rgba(62, 62, 254, 0.1) 0%, rgba(50, 48, 214, 0.1) 100%);
  border: 1px solid rgba(62, 62, 254, 0.2);
  border-radius: 12px;
  font-size: 0.75rem;
  color: #3e3efe;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
}

.inmail-modal__form-group {
  margin-bottom: 20px;
}

.inmail-modal__form-group:last-of-type {
  margin-bottom: 0;
}

.inmail-modal__label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #1a1a2e;
  margin-bottom: 8px;
}

.inmail-modal__required {
  font-weight: 400;
  color: #dc2626;
  font-size: 0.8125rem;
}

.inmail-modal__input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  font-size: 0.875rem;
  font-family: inherit;
  outline: none;
  transition: border-color 0.2s;
}

.inmail-modal__input:focus {
  border-color: #3e3efe;
}

.inmail-modal__input:disabled {
  background-color: #f9fafb;
  cursor: not-allowed;
}

.inmail-modal__input::placeholder {
  color: #9ca3af;
}

.inmail-modal__textarea-wrapper {
  position: relative;
  margin-bottom: 8px;
}

.inmail-modal__textarea {
  width: 100%;
  min-height: 200px;
  padding: 12px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  font-size: 0.875rem;
  font-family: inherit;
  resize: vertical;
  outline: none;
  transition: border-color 0.2s;
}

.inmail-modal__textarea:focus {
  border-color: #3e3efe;
}

.inmail-modal__textarea:disabled {
  background-color: #f9fafb;
  cursor: not-allowed;
}

.inmail-modal__textarea::placeholder {
  color: #9ca3af;
}

.inmail-modal__ai-row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  margin-top: 8px;
}

.inmail-modal__ai-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
}

.inmail-modal__ai-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 24px;
  padding: 4px 10px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.7rem;
  font-weight: 500;
  flex-shrink: 0;
  white-space: nowrap;
}

.inmail-modal__ai-btn--refine {
  background: rgba(107, 114, 128, 0.12);
  color: #4b5563;
}

.inmail-modal__ai-btn--refine:hover:not(:disabled) {
  background: rgba(107, 114, 128, 0.2);
  color: #374151;
}

.inmail-modal__ai-btn--refine .inmail-modal__ai-btn-icon {
  color: #4b5563;
}

.inmail-modal__ai-btn--refine:hover:not(:disabled) .inmail-modal__ai-btn-icon {
  color: #374151;
}

.inmail-modal__ai-btn--write {
  background: rgba(62, 62, 254, 0.12);
  color: #3e3efe;
}

.inmail-modal__ai-btn--write:hover:not(:disabled) {
  background: rgba(62, 62, 254, 0.18);
  color: #2d2dff;
}

.inmail-modal__ai-btn--write .inmail-modal__ai-btn-icon {
  color: #3e3efe;
}

.inmail-modal__ai-btn--write:hover:not(:disabled) .inmail-modal__ai-btn-icon {
  color: #2d2dff;
}

.inmail-modal__ai-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.inmail-modal__ai-btn-icon {
  font-size: 0.75rem;
}

.inmail-modal__ai-btn-text {
  font-size: 0.7rem;
  font-weight: 500;
  line-height: 1;
}

.inmail-modal__ai-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.inmail-modal__ai-panel-title {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #1a1a2e;
}

.inmail-modal__ai-panel-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s;
}

.inmail-modal__ai-panel-close:hover {
  background: rgba(107, 114, 128, 0.12);
  color: #374151;
}

.inmail-modal__help-text {
  display: block;
  font-size: 0.75rem;
  color: #9ca3af;
  margin-top: 4px;
}

.inmail-modal__error {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #dc2626;
  font-size: 0.875rem;
  margin-top: 16px;
}

.inmail-modal__error-icon {
  font-size: 1.125rem;
  flex-shrink: 0;
}

.inmail-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  width: 100%;
}
</style>
